import { useState } from 'react';

import { MenuItemConfig } from 'common/types';
import { Query } from 'common/graphqlTypes';
import Dropdown from 'components/common/Dropdown/Dropdown';
import { ActionMenuItem, ActionsExecutor, DataRow, EventAction } from 'plugins/types';

import './DVActionsButton.scss';

/**
 * Translates EventAction list of actions to menu items, with or without categories
 * @param actionsList             list of actions in menu
 * @param actionSelectedCallback  callback for selecting action
 * @param categories              categories to display actions in sections (optional)
 */
export const getActionsMenuItems = (
  actionsList: ActionMenuItem[],
  actionSelectedCallback: (action: EventAction) => void,
  categories?: string[],
) => {
  const mapActionsToMenuItems = (actions: ActionMenuItem[]): MenuItemConfig[] =>
    actions.map((action) => ({
      text: action.label,
      key: action.actionName || action.actionId,
      disabled: action.disabled,
      clickHandler: () => actionSelectedCallback(action),
      disabledTooltip: action.disabledReason,
    }));

  if (categories) {
    return categories
      .map((category) => ({
        text: category,
        key: category,
        as: 'a', // So items will have proper styling / behaviour
        subMenuItems: mapActionsToMenuItems(actionsList.filter((action) => action.category === category)),
      }))
      .filter((category) => category.subMenuItems?.length > 0);
  }

  return mapActionsToMenuItems(actionsList);
};

/**
 * This component displays a menu for possible actions
 *
 * @prop param.values        event values (log entry values)
 * @prop param.initialData   additional data passed from outer component (defined by dataSource)
 */
function DVActionsButton({
  actionsExecutor: ActionsExecutorComponent,
  actionsList,
  categories,
  label,
  initialData,
  values,
}: {
  actionsExecutor: ActionsExecutor;
  actionsList: ActionMenuItem[];
  categories?: string[];
  label: string;
  initialData?: Partial<Query>;
  values: DataRow;
}) {
  const [selectedAction, setSelectedAction] = useState<EventAction | null>(null);

  const handleActionCancelled = () => {
    setSelectedAction(null);
  };
  const handleActionSelected = (action: EventAction) => {
    setSelectedAction(action);
  };

  const actionsMenuSections: MenuItemConfig[] = getActionsMenuItems(actionsList, handleActionSelected, categories);

  return (
    <>
      <ActionsExecutorComponent
        action={selectedAction}
        actionCompleteCallback={handleActionCancelled}
        values={values}
        initialData={initialData}
      />
      <Dropdown className="DVActionsButton__Dropdown" sections={[actionsMenuSections]} size="sm">
        {label}
      </Dropdown>
    </>
  );
}

export default DVActionsButton;
